/**
 * Pure functions for field operations in screen editor
 *
 * All functions are pure (no side effects) and immutable (return new objects).
 * These are the building blocks for the editor field system.
 *
 * Sprint: screen-editor-header
 * Task: Phase 1 - Pure Functions
 */

import type { FieldType, DisableBehavior } from '../types/editorFieldTypes';

/**
 * Get value from object by dot-notation path
 *
 * @param obj - Source object
 * @param path - Dot-notation path (e.g., 'a.b.c' or 'items.0.name')
 * @returns Value at path or undefined if not found
 *
 * @example
 * getValueByPath({ a: { b: 1 } }, 'a.b') // => 1
 * getValueByPath({ items: [{ name: 'test' }] }, 'items.0.name') // => 'test'
 */
export function getValueByPath<T = unknown>(obj: unknown, path: string): T | undefined {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (path === '') {
    return obj as T;
  }

  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (typeof current !== 'object') {
      return undefined;
    }

    current = (current as Record<string, unknown>)[key];
  }

  return current as T;
}

/**
 * Set value in object by dot-notation path (immutable)
 *
 * Creates a new object with the value set at the specified path.
 * Intermediate objects are created if they don't exist.
 *
 * @param obj - Source object
 * @param path - Dot-notation path
 * @param value - Value to set
 * @returns New object with value set at path
 *
 * @example
 * setValueByPath({ a: { b: 1 } }, 'a.b', 2) // => { a: { b: 2 } }
 */
export function setValueByPath<T>(obj: T, path: string, value: unknown): T {
  if (path === '') {
    return value as T;
  }

  const keys = path.split('.');
  return setValueByPathRecursive(obj, keys, 0, value) as T;
}

function setValueByPathRecursive(
  obj: unknown,
  keys: string[],
  index: number,
  value: unknown
): unknown {
  const key = keys[index];
  const isLastKey = index === keys.length - 1;

  // Determine if we're dealing with an array index
  const currentObj = obj as Record<string, unknown> | unknown[] | null | undefined;
  const isArray = Array.isArray(currentObj);

  // Create base for cloning
  let result: Record<string, unknown> | unknown[];
  if (isArray) {
    result = [...currentObj];
  } else if (currentObj !== null && currentObj !== undefined && typeof currentObj === 'object') {
    result = { ...currentObj };
  } else {
    // Create new object or array based on key type
    const isNumericKey = /^\d+$/.test(key);
    result = isNumericKey ? [] : {};
  }

  if (isLastKey) {
    if (Array.isArray(result)) {
      (result as unknown[])[parseInt(key, 10)] = value;
    } else {
      (result as Record<string, unknown>)[key] = value;
    }
  } else {
    const nextObj = (result as Record<string, unknown>)[key];
    if (Array.isArray(result)) {
      (result as unknown[])[parseInt(key, 10)] = setValueByPathRecursive(nextObj, keys, index + 1, value);
    } else {
      (result as Record<string, unknown>)[key] = setValueByPathRecursive(nextObj, keys, index + 1, value);
    }
  }

  return result;
}

/**
 * Get localized value for a specific locale
 *
 * Looks for l10n object at the path and returns value for the locale.
 *
 * @param obj - Source object
 * @param basePath - Path to the object containing l10n (e.g., 'title' or 'navigationBar.back.content')
 * @param locale - Locale code (e.g., 'en-US')
 * @returns Localized string value or empty string if not found
 *
 * @example
 * getL10nValue({ title: { l10n: { 'en-US': 'Hello' } } }, 'title', 'en-US') // => 'Hello'
 */
export function getL10nValue(obj: unknown, basePath: string, locale: string): string {
  // Handle case where basePath already ends with .l10n
  const l10nPath = basePath.endsWith('.l10n') ? basePath : `${basePath}.l10n`;
  const l10nObj = getValueByPath<Record<string, string>>(obj, l10nPath);

  if (!l10nObj || typeof l10nObj !== 'object') {
    return '';
  }

  return l10nObj[locale] ?? '';
}

/**
 * Set localized value for a specific locale (immutable)
 *
 * Creates or updates the l10n object at the specified path.
 *
 * @param obj - Source object
 * @param basePath - Path to the object containing l10n
 * @param locale - Locale code
 * @param value - String value to set
 * @returns New object with l10n value set
 *
 * @example
 * setL10nValue({ title: { l10n: {} } }, 'title', 'en-US', 'Hello')
 * // => { title: { l10n: { 'en-US': 'Hello' } } }
 */
export function setL10nValue<T>(obj: T, basePath: string, locale: string, value: string): T {
  // Get existing l10n object or create empty one
  const l10nPath = basePath.endsWith('.l10n') ? basePath : `${basePath}.l10n`;
  const existingL10n = getValueByPath<Record<string, string>>(obj, l10nPath) ?? {};

  // Create new l10n object with updated locale
  const newL10n = {
    ...existingL10n,
    [locale]: value,
  };

  return setValueByPath(obj, l10nPath, newL10n);
}

/**
 * Check if a field is enabled (has a non-null, non-undefined value)
 *
 * @param obj - Source object
 * @param path - Path to check
 * @returns true if value exists and is not null/undefined
 *
 * @example
 * isFieldEnabled({ field: 'value' }, 'field') // => true
 * isFieldEnabled({ field: null }, 'field') // => false
 */
export function isFieldEnabled(obj: unknown, path: string): boolean {
  const value = getValueByPath(obj, path);
  if (value === null || value === undefined) return false;
  // Treat schema-valid empty Text as disabled: has `kind` field + empty l10n {}
  // Does NOT apply to Image objects (no `kind` field) — those use null/non-null check
  if (
    typeof value === 'object' &&
    'kind' in (value as Record<string, unknown>) &&
    'l10n' in (value as Record<string, unknown>) &&
    typeof (value as Record<string, unknown>).l10n === 'object' &&
    (value as Record<string, unknown>).l10n !== null &&
    Object.keys((value as Record<string, unknown>).l10n as object).length === 0
  ) {
    return false;
  }
  return true;
}

/**
 * Get default value for a field type
 *
 * Used as fallback when both current and template values are undefined.
 *
 * @param type - Field type
 * @returns Default value for the type
 *
 * @example
 * getTypeDefault('text') // => ''
 * getTypeDefault('number') // => 0
 * getTypeDefault('color') // => '#ffffff'
 */
export function getTypeDefault(type: FieldType): unknown {
  switch (type) {
    case 'text':
      return '';
    case 'number':
      return 0;
    case 'color':
      return '#ffffff';
    case 'select':
      return '';
    case 'slider':
      return 0;
    default:
      return '';
  }
}

/**
 * Enable a field by setting it to template value or default (immutable)
 *
 * @param obj - Source object
 * @param path - Path to the field
 * @param templateValue - Value from template (preferred)
 * @param defaultValue - Fallback if template is null/undefined
 * @returns New object with field enabled
 *
 * @example
 * enableField({ field: null }, 'field', 'template', 'default')
 * // => { field: 'template' }
 */
export function enableField<T>(
  obj: T,
  path: string,
  templateValue: unknown,
  defaultValue: unknown
): T {
  const value = templateValue !== null && templateValue !== undefined
    ? templateValue
    : defaultValue;

  return setValueByPath(obj, path, value);
}

/**
 * Disable a field based on the specified behavior (immutable)
 *
 * @param obj - Source object
 * @param path - Path to the field
 * @param behavior - How to handle the disabled state
 * @param templateValue - Value from template (for 'template' behavior)
 * @param constantValue - Constant value (for 'constant' behavior)
 * @returns New object with field disabled
 *
 * @example
 * disableField({ field: 'value' }, 'field', 'null') // => { field: null }
 * disableField({ field: 'value' }, 'field', 'template', 'tpl') // => { field: 'tpl' }
 * disableField({ field: 'value' }, 'field', 'constant', undefined, 'CONST') // => { field: 'CONST' }
 */
export function disableField<T>(
  obj: T,
  path: string,
  behavior: DisableBehavior,
  templateValue?: unknown,
  constantValue?: unknown
): T {
  let value: unknown;

  switch (behavior) {
    case 'null':
      value = null;
      break;
    case 'template':
      value = templateValue !== undefined ? templateValue : null;
      break;
    case 'constant':
      value = constantValue !== undefined ? constantValue : null;
      break;
    default:
      value = null;
  }

  return setValueByPath(obj, path, value);
}
